import React from 'react';
import { Box, Text, Badge, Input } from '@chakra-ui/react';
import {
  IMetricDef,
  ICatalogResponse,
} from '../../../../models/sessions';
import { ICsvMappingEntryFE } from '../../../../endpoints/sessions';

interface ColumnMappingTableProps {
  columns: string[];
  sampleRows: Record<string, string>[];
  mapping: ICsvMappingEntryFE[];
  onChange: (mapping: ICsvMappingEntryFE[]) => void;
  catalog: ICatalogResponse | undefined;
  suggestions: Record<string, string>;
  templateApplied?: boolean;
}

/**
 * Tabella mapping colonne CSV → metrica LEOPART (Step 3 wizard).
 * Ogni riga: nome col CSV | sample 3 valori | dropdown metrica | unità | ignore.
 */
export const ColumnMappingTable: React.FC<ColumnMappingTableProps> = ({
  columns,
  sampleRows,
  mapping,
  onChange,
  catalog,
  suggestions,
  templateApplied,
}) => {
  if (!catalog) return null;

  const updateRow = (
    csvColumnIndex: number,
    patch: Partial<ICsvMappingEntryFE>,
  ) => {
    const newMapping = mapping.map((m) =>
      m.csvColumnIndex === csvColumnIndex ? { ...m, ...patch } : m,
    );
    onChange(newMapping);
  };

  return (
    <Box overflowX="auto">
      <Box
        as="table"
        width="100%"
        style={{ borderCollapse: 'collapse', fontSize: '13px' }}
      >
        <thead>
          <tr style={{ background: '#f1f5f9' }}>
            <th style={{ padding: 8, textAlign: 'left', minWidth: 180 }}>
              Colonna CSV
            </th>
            <th style={{ padding: 8, textAlign: 'left' }}>Sample valori</th>
            <th style={{ padding: 8, textAlign: 'left', minWidth: 240 }}>
              → Metrica LEOPART
            </th>
            <th style={{ padding: 8, textAlign: 'left', minWidth: 90 }}>
              Unità
            </th>
            <th style={{ padding: 8 }}>Ignora</th>
          </tr>
        </thead>
        <tbody>
          {columns.map((col, idx) => {
            const entry = mapping.find((m) => m.csvColumnIndex === idx);
            const samples = sampleRows
              .slice(0, 3)
              .map((r) => r[col])
              .filter((v) => v !== undefined && v !== '');

            const status = !entry?.leopartMetricId
              ? 'unmapped'
              : entry.ignored
              ? 'ignored'
              : templateApplied
              ? 'auto'
              : suggestions[col] === entry.leopartMetricId
              ? 'suggested'
              : 'manual';

            const statusBadge = {
              unmapped: { color: 'orange', label: 'DA MAPPARE' },
              ignored: { color: 'gray', label: 'IGNORA' },
              auto: { color: 'green', label: 'AUTO' },
              suggested: { color: 'blue', label: 'SUGGERITO' },
              manual: { color: 'green', label: 'MANUALE' },
            }[status];

            return (
              <tr
                key={idx}
                style={{
                  borderBottom: '1px solid #e2e8f0',
                  background: entry?.ignored ? '#f8fafc' : 'white',
                }}
              >
                <td style={{ padding: 8 }}>
                  <Text fontFamily="mono" fontSize="13px" fontWeight="500">
                    {col}
                  </Text>
                  <Badge mt={1} colorPalette={statusBadge.color} size="sm">
                    {statusBadge.label}
                  </Badge>
                </td>
                <td style={{ padding: 8 }}>
                  <Text fontSize="12px" color="gray.500" fontFamily="mono">
                    {samples.slice(0, 3).join(' · ')}
                  </Text>
                </td>
                <td style={{ padding: 8 }}>
                  <select
                    value={entry?.leopartMetricId ?? ''}
                    onChange={(e) =>
                      updateRow(idx, { leopartMetricId: e.target.value })
                    }
                    disabled={entry?.ignored}
                    style={{
                      width: '100%',
                      padding: '6px 8px',
                      borderRadius: 6,
                      border: '1px solid #cbd5e1',
                      fontSize: 13,
                      background: 'var(--c-card-bg)',
                    }}
                  >
                    <option value="">— scegli —</option>
                    <optgroup label="Identificativi">
                      <option value="athlete">Atleta</option>
                      <option value="date">Data</option>
                      <option value="session_type">Tipo sessione</option>
                    </optgroup>
                    {(['identification', 'internal', 'metabolic', 'volume', 'events', 'advanced', 'running', 'cycling', 'endurance_common'] as const).map(
                      (group) => {
                        const groupMetrics = catalog.metrics.filter(
                          (m) => m.group === group,
                        );
                        if (groupMetrics.length === 0) return null;
                        return (
                          <optgroup key={group} label={group}>
                            {groupMetrics.map((m: IMetricDef) => (
                              <option key={m.id} value={m.id}>
                                {m.label} ({m.unit})
                              </option>
                            ))}
                          </optgroup>
                        );
                      },
                    )}
                  </select>
                </td>
                <td style={{ padding: 8 }}>
                  <Input
                    size="sm"
                    placeholder="m/km/min"
                    value={entry?.unit ?? ''}
                    onChange={(e) => updateRow(idx, { unit: e.target.value })}
                    disabled={entry?.ignored}
                    width="80px"
                  />
                </td>
                <td style={{ padding: 8, textAlign: 'center' }}>
                  <input
                    type="checkbox"
                    checked={entry?.ignored ?? false}
                    onChange={(e) =>
                      updateRow(idx, { ignored: e.target.checked })
                    }
                  />
                </td>
              </tr>
            );
          })}
        </tbody>
      </Box>
    </Box>
  );
};
